import React from "react";

import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import styles from "./VoterListFacets.module.styl";

const EXCLUSION_TAGS = Object.freeze({
  exclude: {
    label: t("filter.exclude"),
    aliases: ["exclude", "excluded", "ignore"]
  },
  unresponsive: {
    label: t("filter.unresponsive"),
    aliases: ["unresponsive"]
  }
});

const EXCLUSION_TAG_STATES = Object.freeze([
  { value: "exclude", label: t("filter.hide") },
  { value: "include", label: t("filter.include") },
  { value: "only", label: t("filter.only") }
]);

const DEFAULT_EXCLUSION_TAG_FILTERS = Object.freeze(
  Object.fromEntries(Object.keys(EXCLUSION_TAGS).map((key) => [key, "exclude"]))
);

export const DEFAULT_VOTER_LIST_FILTERS = Object.freeze({
  gender: [],
  minAge: null,
  maxAge: null,
  exclusionTags: DEFAULT_EXCLUSION_TAG_FILTERS
});

const GENDER_OPTIONS = [
  { value: "male", label: t("filter.male") },
  { value: "female", label: t("filter.female") },
  { value: "unknown", label: t("common.unknown") }
];

const normalizeArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => String(item || "").trim()).filter(Boolean);
};

const normalizeChoice = (value, allowed, fallback) => {
  return allowed.includes(value) ? value : fallback;
};

const normalizeAge = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const number = Number(value);
  if (!Number.isFinite(number)) {
    return null;
  }

  return Math.max(0, Math.min(150, Math.floor(number)));
};

const normalizeExclusionTagFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const out = {};

  for (const key of Object.keys(EXCLUSION_TAGS)) {
    out[key] = normalizeChoice(input[key], ["exclude", "include", "only"], DEFAULT_EXCLUSION_TAG_FILTERS[key]);
  }

  return out;
};

export const normalizeVoterListFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value) ? value : {};

  return {
    gender: normalizeArray(input.gender).filter((item) => ["male", "female", "unknown"].includes(item)),
    minAge: normalizeAge(input.minAge),
    maxAge: normalizeAge(input.maxAge),
    exclusionTags: normalizeExclusionTagFilters(input.exclusionTags)
  };
};

export const isDefaultVoterListFilters = (value) => {
  const filters = normalizeVoterListFilters(value);
  const exclusionDefaults = Object.keys(EXCLUSION_TAGS).every(
    (key) => filters.exclusionTags[key] === DEFAULT_EXCLUSION_TAG_FILTERS[key]
  );

  return filters.gender.length === 0 &&
    filters.minAge === null &&
    filters.maxAge === null &&
    exclusionDefaults;
};

export const getVoterListActiveFilterCount = (value) => {
  const filters = normalizeVoterListFilters(value);
  let count = filters.gender.length;

  if (filters.minAge !== null || filters.maxAge !== null) {
    count += 1;
  }

  for (const key of Object.keys(EXCLUSION_TAGS)) {
    if (filters.exclusionTags[key] !== DEFAULT_EXCLUSION_TAG_FILTERS[key]) {
      count += 1;
    }
  }

  return count;
};

export const buildVoterFilterPayload = (value) => normalizeVoterListFilters(value);

export const loadVoterFacetCounts = async ({ search, filters }) => {
  return await rest.request(getServiceUri() + "voter-list/facets", "POST", {
    search: String(search || "").trim(),
    filters: normalizeVoterListFilters(filters)
  });
};

const countFor = (counts, path, fallback = 0) => {
  let current = counts || {};

  for (const key of path) {
    if (!current || typeof current !== "object") {
      return fallback;
    }

    current = current[key];
  }

  const number = Number(current);
  return Number.isFinite(number) ? number : fallback;
};

const toggleArrayValue = (values, value) => {
  const current = normalizeArray(values);

  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
};

const FacetSection = ({ title, children }) => (
  <section className={styles.section}>
    <div className={styles.sectionTitle}>{title}</div>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

const CheckboxOption = ({ label, selected, count, disabled, onClick }) => (
  <button
    type="button"
    className={selected ? `${styles.option} ${styles.optionSelected}` : styles.option}
    disabled={disabled}
    onClick={onClick}
  >
    <span className={styles.checkBox}>{selected ? "✓" : ""}</span>
    <span className={styles.optionLabel}>{label}</span>
    {count !== undefined ? <span className={styles.count}>{count}</span> : null}
  </button>
);

const Chip = ({ label, onRemove }) => (
  <button type="button" className={styles.chip} onClick={onRemove}>
    <span>{label}</span>
    <span className={styles.chipClose}>×</span>
  </button>
);

const setExclusionTagState = (filters, key, state) => ({
  exclusionTags: {
    ...filters.exclusionTags,
    [key]: state
  }
});

export const VoterListFacetButton = ({ controller }) => {
  const activeCount = controller?.activeCount || 0;

  return (
    <button
      type="button"
      className={activeCount ? `${styles.filterButton} ${styles.filterButtonActive}` : styles.filterButton}
      onClick={controller.toggleDrawer}
    >
      <span className={styles.filterButtonIcon}><TuneIcon fontSize="inherit" /></span>
      <span className={styles.filterButtonLabel}>{t("common.filters")}</span>
      {activeCount ? <span className={styles.filterButtonBadge}>{activeCount}</span> : null}
    </button>
  );
};

export const VoterListFacetChips = ({ controller }) => {
  const filters = normalizeVoterListFilters(controller?.value);

  if (isDefaultVoterListFilters(filters)) {
    return null;
  }

  const set = (patch) => controller.patchValue(patch);

  return (
    <div className={styles.chipsBar}>
      {filters.gender.map((value) => (
        <Chip
          key={`gender-${value}`}
          label={`${t("filter.gender")}: ${GENDER_OPTIONS.find((item) => item.value === value)?.label || value}`}
          onRemove={() => set({ gender: filters.gender.filter((item) => item !== value) })}
        />
      ))}

      {filters.minAge !== null ? (
        <Chip
          label={`${t("filter.minimumAge")}: ${filters.minAge}`}
          onRemove={() => set({ minAge: null })}
        />
      ) : null}

      {filters.maxAge !== null ? (
        <Chip
          label={`${t("filter.maximumAge")}: ${filters.maxAge}`}
          onRemove={() => set({ maxAge: null })}
        />
      ) : null}

      {Object.keys(EXCLUSION_TAGS).map((key) => {
        const state = filters.exclusionTags[key];

        if (state === DEFAULT_EXCLUSION_TAG_FILTERS[key]) {
          return null;
        }

        const label = EXCLUSION_TAGS[key].label;
        const stateLabel = EXCLUSION_TAG_STATES.find((item) => item.value === state)?.label || state;

        return (
          <Chip
            key={`exclusion-${key}`}
            label={`${label}: ${stateLabel}`}
            onRemove={() => set(setExclusionTagState(filters, key, DEFAULT_EXCLUSION_TAG_FILTERS[key]))}
          />
        );
      })}

      <button type="button" className={styles.clearAll} onClick={controller.reset}>{t("common.clearAll")}</button>
    </div>
  );
};

const AgeRangeFilter = ({ filters, onSet }) => (
  <div className={styles.ageRange}>
    <label className={styles.ageField}>
      <span className={styles.ageFieldLabel}>{t("filter.minimumAge")}</span>
      <input
        type="number"
        min="0"
        max="150"
        step="1"
        className={styles.ageInput}
        value={filters.minAge ?? ""}
        onChange={(event) => onSet({ minAge: normalizeAge(event.target.value) })}
      />
    </label>

    <label className={styles.ageField}>
      <span className={styles.ageFieldLabel}>{t("filter.maximumAge")}</span>
      <input
        type="number"
        min="0"
        max="150"
        step="1"
        className={styles.ageInput}
        value={filters.maxAge ?? ""}
        onChange={(event) => onSet({ maxAge: normalizeAge(event.target.value) })}
      />
    </label>
  </div>
);

const ExclusionTagFilter = ({ filters, counts, tagKey, onSet }) => {
  const config = EXCLUSION_TAGS[tagKey];
  const selected = filters.exclusionTags[tagKey];
  const count = countFor(counts, ["exclusions", tagKey]);

  return (
    <div className={styles.exclusionCompactRow}>
      <div className={styles.exclusionCompactMeta}>
        <span className={styles.exclusionCompactName}>{config.label}</span>
        <span className={styles.exclusionCompactCount}>{count}</span>
      </div>

      <div className={styles.segmentedControl}>
        {EXCLUSION_TAG_STATES.map((state) => (
          <button
            key={`${tagKey}-${state.value}`}
            type="button"
            className={selected === state.value ? `${styles.segmentedButton} ${styles.segmentedButtonSelected}` : styles.segmentedButton}
            onClick={() => onSet(tagKey, state.value)}
          >
            {state.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const ExclusionsSection = ({ filters, counts, onSet }) => {
  const [open, setOpen] = React.useState(false);
  const changedCount = Object.keys(EXCLUSION_TAGS).filter(
    (key) => filters.exclusionTags[key] !== DEFAULT_EXCLUSION_TAG_FILTERS[key]
  ).length;

  return (
    <section className={styles.exclusionCollapsedSection}>
      <button type="button" className={styles.exclusionCollapseButton} onClick={() => setOpen((value) => !value)}>
        <span className={styles.exclusionCollapseText}>{t("filter.exclusions")}</span>
        {changedCount ? <span className={styles.exclusionChangedBadge}>{changedCount}</span> : null}
        <span className={open ? `${styles.exclusionChevron} ${styles.exclusionChevronOpen}` : styles.exclusionChevron}>
          <ExpandMoreIcon fontSize="inherit" />
        </span>
      </button>

      {open ? (
        <div className={styles.exclusionCollapsedBody}>
          <div className={styles.exclusionCompactHelp}>
            {t("filter.exclusionHelp")}
          </div>

          {Object.keys(EXCLUSION_TAGS).map((tagKey) => (
            <ExclusionTagFilter
              key={tagKey}
              filters={filters}
              counts={counts}
              tagKey={tagKey}
              onSet={onSet}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
};

const VoterListFacets = ({ controller }) => {
  const filters = normalizeVoterListFilters(controller?.value);
  const counts = controller?.counts || {};
  const set = (patch) => controller.patchValue(patch);
  const setExclusion = (key, state) => set(setExclusionTagState(filters, key, state));

  return (
    <div className={styles.drawer}>
      <div className={styles.drawerHeader}>
        <div>
          <div className={styles.drawerTitle}>{t("common.filters")}</div>
          <div className={styles.drawerSubtitle}>{controller.countsLoading ? t("common.updatingCounts") : t("filter.refineVoters")}</div>
        </div>

        <button type="button" className={styles.closeButton} onClick={controller.closeDrawer} aria-label={t("common.closeFilters")}>
          <CloseIcon fontSize="inherit" />
        </button>
      </div>

      <div className={styles.drawerBody}>
        <FacetSection title={t("filter.gender")}>
          {GENDER_OPTIONS.map((option) => (
            <CheckboxOption
              key={option.value}
              label={option.label}
              selected={filters.gender.includes(option.value)}
              count={countFor(counts, ["gender", option.value])}
              onClick={() => set({ gender: toggleArrayValue(filters.gender, option.value) })}
            />
          ))}
        </FacetSection>

        <FacetSection title={t("filter.age")}>
          <AgeRangeFilter filters={filters} onSet={set} />
        </FacetSection>

        <ExclusionsSection filters={filters} counts={counts} onSet={setExclusion} />
      </div>

      <div className={styles.drawerFooter}>
        <button type="button" className={styles.secondaryAction} onClick={controller.reset} disabled={controller.activeCount === 0}>
          <RestartAltIcon fontSize="inherit" />
          <span>{t("common.resetFilters")}</span>
        </button>

        <button type="button" className={styles.primaryAction} onClick={controller.closeDrawer}>{t("common.done")}</button>
      </div>
    </div>
  );
};

export const VOTER_LIST_FACETS = {
  storageKey: "haiti.voter.facets.v2",
  overlayLabel: t("filter.voterFilters"),
  defaultValue: DEFAULT_VOTER_LIST_FILTERS,
  normalizeValue: normalizeVoterListFilters,
  isDefaultValue: isDefaultVoterListFilters,
  getActiveCount: getVoterListActiveFilterCount,
  loadCounts: loadVoterFacetCounts,
  renderButton: (controller) => <VoterListFacetButton controller={controller} />,
  renderDrawer: (controller) => <VoterListFacets controller={controller} />,
  renderChips: (controller) => <VoterListFacetChips controller={controller} />
};

export default VoterListFacets;
